import { useTrackPromotion } from '@/hooks/useTrackPromotion';
import type { Ga4Promotion } from '@/types';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

interface SplitLayoutHeroPromoData {
  id: string;
  promotionName?: string;
  creativeName?: string;
}

export const SplitLayoutHeroPromo = ({
  data,
}: {
  data: SplitLayoutHeroPromoData;
}) => {
  const { id, promotionName, creativeName } = data;
  const markerRef = useRef<HTMLSpanElement>(null);
  const hostRef = useRef<HTMLElement | null>(null);
  const [ready, setReady] = useState(false);

  const promotion = useMemo<Ga4Promotion>(
    () => ({
      promotion_id: id,
      promotion_name: promotionName || 'Split layout hero',
      creative_name: creativeName,
      creative_slot: 'split-hero',
    }),
    [id, promotionName, creativeName],
  );

  // Gate on `ready` so the observer effect re-runs after hostRef is set.
  const { onSelect } = useTrackPromotion(hostRef, ready && id ? [promotion] : []);

  useLayoutEffect(() => {
    hostRef.current =
      markerRef.current?.closest<HTMLElement>('.nfp13-hero') ?? null;
    setReady(true);
  }, []);

  useEffect(() => {
    const host = hostRef.current;
    if (!ready || !host) {
      return;
    }
    const links = host.querySelectorAll<HTMLElement>(
      '.nfp13-hero-cta, .nfp13-hero-link',
    );
    const handler = () => onSelect(promotion);
    for (const link of links) {
      link.addEventListener('click', handler);
    }
    return () => {
      for (const link of links) {
        link.removeEventListener('click', handler);
      }
    };
  }, [ready, onSelect, promotion]);

  return <span ref={markerRef} style={{ display: 'none' }} aria-hidden="true" />;
};
